import { Flex } from '@chakra-ui/react';
import { useState } from 'react';

import { ButtonIcon, Typography, VARIANTS } from 'shared';
import { DatePicker } from 'shared_DEPRECATED/components/Form/DatePicker';
import { Icon, iconNames } from 'shared_DEPRECATED/components/Icon';
import { dateUtils } from 'shared_DEPRECATED/utils';

import { ScheduledForCallback } from 'features/chat/config/types/ScheduledMessage';

import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  SIZES,
} from 'shared/components/Modal';
import { ModalButton } from 'shared/components/Modal/ModalButton';
import { useModalContext } from 'shared/components/Modal/context';

import './scheduleCalendar.css';

const TIME_INTERVALS = 15;
const MINIMUM_MINUTES_BEFORE_CURRENT_TIME = 6;
const DATE_PICKER_CLASSNAME = 'schedule-date-picker';

type ScheduleModalContentProps = {
  onSubmit: ScheduledForCallback;
  scheduledForDate?: Date;
  onBackButtonClick?: () => void;
};

export const ChatMessageScheduleModalContent = ({
  onSubmit,
  scheduledForDate,
  onBackButtonClick,
}: ScheduleModalContentProps) => {
  const { timezone } = useModalContext();

  // here we check if next 15 minute interval is closer that 6 minutes
  // before the current time we add another 15 minutes, so we don't encounter
  // errors from BE about scheduled date is too close (BE limitation is 5 minutes)
  const getInitialDate = () => {
    const date = dateUtils().ceilMinutes(TIME_INTERVALS).toDate();
    if (
      Math.abs(dateUtils().diff(date, 'minutes')) <
      MINIMUM_MINUTES_BEFORE_CURRENT_TIME
    ) {
      return dateUtils(date).add(TIME_INTERVALS, 'minutes').toDate();
    }
    return date;
  };

  const [scheduledFor, setScheduledFor] = useState<Date>(
    scheduledForDate ? dateUtils(scheduledForDate).toDate() : getInitialDate()
  );

  const isTodayDateSelected = dateUtils(scheduledFor).isToday();

  const handleDateChange = (date: Date) => {
    const preparedDate = dateUtils(date).isToday()
      ? dateUtils().ceilMinutes(TIME_INTERVALS).toDate()
      : dateUtils(date).startOf('day').toDate();

    setScheduledFor(preparedDate);
  };

  const handleSubmit = () => {
    onSubmit(dateUtils(dateUtils.utc(scheduledFor)).formatAsISOString());
  };

  const onTimeSelect = (date: Date) => {
    setScheduledFor(date);
  };

  return (
    <>
      <ModalHeader>
        <Flex gap="var(--spacing01)" alignItems="center">
          {onBackButtonClick && (
            <ButtonIcon
              size="sm"
              bg="transparent"
              _hover={{
                bg: 'transparent',
              }}
              onClick={onBackButtonClick}
              aria-label="back button"
            >
              <Icon name={iconNames.arrowLeft} stroke="var(--fgPrimary)" />
            </ButtonIcon>
          )}
          <Typography type="smallHeading" fontWeight="semibold" color="black">
            Schedule message
          </Typography>
        </Flex>
      </ModalHeader>
      <ModalBody>
        <DatePicker
          //@ts-ignore
          selected={scheduledFor}
          onChange={handleDateChange}
          calendarClassName={DATE_PICKER_CLASSNAME}
          //@ts-ignore
          minDate={dateUtils().toDate()}
          inline
          timeFormat="h:mm a"
        />
        <Flex
          direction="column"
          alignItems="center"
          m="0 auto"
          pb={2}
          gap={2.5}
          pt={8}
        >
          <Typography type="meta" color="black" fontWeight="medium">
            Your time:
          </Typography>
          <DatePicker
            selected={scheduledFor}
            onChange={onTimeSelect}
            showTimeSelect
            showTimeSelectOnly
            timeCaption="Time"
            timeInputLabel="Time"
            anchorIcon={null}
            timeIntervals={TIME_INTERVALS}
            dateFormat="h:mm aa"
            filterTime={(time: string) => {
              const currentDate = dateUtils();
              return currentDate.isBefore(time);
            }}
          />
          <Flex gap="var(--spacing01)">
            <Typography type="meta" color="black" fontWeight="medium">
              Participant time:
            </Typography>
            <Typography type="meta" color="gray" fontWeight="medium">
              {dateUtils.utc(scheduledFor).tz(timezone).format('h:mm a')}
            </Typography>
          </Flex>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <ModalButton
          color={VARIANTS.PRIMARY}
          size={SIZES.SM}
          w="100%"
          onClick={handleSubmit}
        >
          {`Schedule ${
            isTodayDateSelected
              ? 'today'
              : 'on ' + dateUtils(scheduledFor).format('D MMM')
          }`}
        </ModalButton>
      </ModalFooter>
    </>
  );
};
